import {
  Element,
  InstallSpec,
  ItemComponent,
} from "@p0security/shared/install/types";
import { last } from "lodash-es";

import { GCLOUD_SECURITY_PERIMETER_FEATURE_FLAG } from "./constants.ts";
import { gcloudProjectValidator } from "./validator.ts";

const iamAssessmentDescription =
  "Allows P0 to analyze your IAM configuration for security issues. Required for IAM Posture and Access Inventory.";

const GCLOUD_ORGANIZATION_PATTERN = /^[0-9]+$/;

const HTTPS_URL_PATTERN = /^https:\/\/[a-zA-Z0-9-.]+$/;

const gcloudOrganizationValidator = async (_id: string, field: string) => {
  if (!field.match(GCLOUD_ORGANIZATION_PATTERN))
    return "Google Cloud organization identifiers must consist of numerals only";
};

const gcloudSecurityPerimeterUrl = async (_id: string, field: string) => {
  if (last(field) === "/") return "url must not end with a slash";
  if (!field.match(HTTPS_URL_PATTERN)) return "url must use https://";
};

export const GcloudComponents = InstallSpec({
  "access-logs": ItemComponent({
    label: "Access logging",
    description:
      "Enhance IAM assessments with continuous usage data from your projects.",
    type: "dynamic",
    maturity: "ga",
    schema: {
      "pre-20240729-pubsub-ingest": Element({
        type: "hidden",
        label: "Legacy Pub/Sub ingest table name",
      }),
    },
  }),
  "iam-assessment": ItemComponent({
    label: "IAM assessment",
    description: iamAssessmentDescription,
    type: "string",
    maturity: "ga",
    schema: {},
    validator: gcloudProjectValidator,
  }),
  "iam-write": ItemComponent({
    label: "IAM management",
    description:
      "Allows P0 to grant and revoke Google Cloud access for your users.",
    type: "string",
    maturity: "ga",
    schema: {},
    validator: gcloudProjectValidator,
  }),
  "org-wide-policy": ItemComponent({
    label: "Organization policies",
    description:
      "Allows P0 to analyze your organization and folder IAM policies for security issues.",
    type: "singleton",
    maturity: "ga",
    schema: {},
  }),
  "sharing-restriction": ItemComponent({
    label: "Domain-restricted sharing",
    description: "Prevent privilege escalation via P0's integration.",
    type: "dynamic",
    maturity: "ga",
    schema: {},
  }),
  "org-access-logs": ItemComponent({
    label: "Organization access logging",
    description:
      "Enhance IAM assessments with continuous usage data from your entire organization.",
    type: "singleton",
    maturity: "ga",
    schema: {
      "pre-20240729-pubsub-ingest": Element({
        type: "hidden",
        label: "Legacy Pub/Sub ingest table name",
      }),
    },
  }),
  "org-iam-assessment": ItemComponent({
    label: "Organization IAM assessment",
    description: `${iamAssessmentDescription} Installed for every project in your organization.`,
    type: "singleton",
    maturity: "ga",
    schema: {},
  }),
  "key-rotation": ItemComponent({
    type: "singleton",
    label: "Key rotation",
    description: "Allows P0 rotate your service account keys",
    hidden: true,
    maturity: "ga",
    schema: {
      runUrl: Element({
        label:
          "URL of Cloud Run service that exposes functions for deletion, disabling, and rotation",
        type: "string",
      }),
    },
  }),
  root: ItemComponent({
    label: "Google Cloud organization",
    type: "singleton",
    maturity: "ga",
    schema: {
      organizationId: Element({
        label: "Organization ID",
        type: "string",
        validator: gcloudOrganizationValidator,
      }),
      serviceAccountEmail: Element({
        label: "Service account",
        type: "hidden",
      }),
      impersonateViaEmail: Element({
        label: "Intermediate service account",
        description:
          "If present, service account to impersonate prior to impersonating serviceAccountEmail",
        type: "hidden",
      }),
    },
  }),
  "iam-write-security-perimeter": ItemComponent({
    label: "IAM management security perimeter",
    description:
      "Provides a secure mechanism for P0 to make changes to your projects' IAM configurations.",
    type: "string",
    maxItems: 1,
    maturity: "ga",
    validator: gcloudProjectValidator,
    schema: {
      allowedDomains: Element({
        label: "Allowed domains",
        description:
          "Only users with these email domains may be granted access to your projects",
        type: "generated",
      }),
      imageDigest: Element({
        label: "Image tag and digest",
        description:
          "The tag and digest of the Docker image to use for the security perimeter",
        type: "generated",
      }),
      region: Element({
        label: "Region",
        description:
          "The GCP region where the Cloud Run security perimeter service is deployed (e.g., us-west1)",
        type: "string",
        step: "new",
      }),
      cloudRunUrl: Element({
        label: "Security perimeter URL",
        description:
          "The URL of the Cloud Run service that enforces the security perimeter",
        type: "string",
        validator: gcloudSecurityPerimeterUrl,
      }),
    },
    featureFlag: GCLOUD_SECURITY_PERIMETER_FEATURE_FLAG,
  }),
  "function-caller": ItemComponent({
    label: "Cloud Run invocation",
    description:
      "Allows P0 to invoke Cloud Run Services in your Google Cloud account. Configure your Cloud Run Service as a webhook in other integrations such as the Custom Resource integration.",
    type: "string",
    maturity: "ga",
    schema: {
      fullName: Element({
        label: "Full name",
        type: "generated",
        description:
          "The full name of the Cloud Run Service, including the project ID and region",
      }),
      url: Element({
        label: "Service URL",
        type: "string",
        description:
          "The URL of the Cloud Run Service that exposes functions for invocation",
        step: "new",
      }),
      projectId: Element({
        label: "Project identifier",
        description:
          "The project ID of the Google Cloud project that contains the Cloud Run Service",
        type: "string",
        step: "new",
      }),
      region: Element({
        label: "Region",
        description:
          "The region where the Cloud Run Service is deployed. For example, us-central1",
        type: "string",
        step: "new",
      }),
    },
  }),
});
